import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CustomCursor from '../components/CustomCursor';
import SoundToggle from '../components/SoundToggle';
import { 
  db, auth, chaptersCollection, manifestoLinesCollection, sectionsCollection,
  addressesCollection,
  getDocs, getDoc, setDoc, addDoc, doc, query, orderBy, where
} from '../firebase/config';
import logoImage from '../images/logo-removebg-preview.png';
import './HomePage.css';
import { 
  playClickSound, 
  playSuccessSound, 
  playErrorSound, 
  playUnlockSound,
  playPageLoadSound,
  stopLoadingSound
} from '../utils/soundEffects';

const HomePage = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedChapterIndex, setSelectedChapterIndex] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [manifestoLines, setManifestoLines] = useState([
    "AWARENESS IS RESISTANCE.",
    "We reject inherited identity.",
    "We reject unconscious conformity disguised as individuality.",
    "We reject existence performed for approval.",
    "THE SELF SHOULD BE CONSTRUCTED INTENTIONALLY."
  ]);
  const [currentManifestoIndex, setCurrentManifestoIndex] = useState(0);
  const [manifestoVisible, setManifestoVisible] = useState(true);
  const [unlockedChapters, setUnlockedChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentChapter, setPaymentChapter] = useState(null);
  const [paymentIndex, setPaymentIndex] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [userPurchases, setUserPurchases] = useState([]);
  const [profileLoading, setProfileLoading] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddAddressForm, setShowAddAddressForm] = useState(false);
  const [newAddress, setNewAddress] = useState({
    fullName: '',
    phone: '',
    addressLine: '',
    city: '',
    state: '',
    pincode: '',
    isDefault: false
  });
  const navMenuRef = useRef(null);
  
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.skullture.co.in';

  // Load Razorpay script
  useEffect(() => {
    if (window.Razorpay) return;
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      // Don't remove script
    };
  }, []);

  useEffect(() => {
    playPageLoadSound();
    return () => {
      stopLoadingSound();
    };
  }, []);

  useEffect(() => {
    for(let i = 0; i < 50; i++) {
      const particle = document.createElement('div');
      particle.classList.add('particle');
      particle.style.left = Math.random() * 100 + '%';
      particle.style.animationDelay = Math.random() * 8 + 's';
      particle.style.animationDuration = 5 + Math.random() * 5 + 's';
      particle.style.width = Math.random() * 3 + 1 + 'px';
      particle.style.height = particle.style.width;
      document.body.appendChild(particle);
    }
    
    return () => {
      document.querySelectorAll('.particle').forEach(p => p.remove());
    };
  }, []);

  // Dynamic content states
  const [topBar1Content, setTopBar1Content] = useState('⚡ NEW SIGNAL: LATEST CHAPTERS AVAILABLE ⚡ FREE SHIPPING ON ARTIFACTS ABOVE ₹1499 ⚡ USE CODE: SKULL15 ⚡');
  const [topBar2Content, setTopBar2Content] = useState('⛧ EACH DROP DELIBERATE EACH PIECE A STATEMENT ⛧ IDENTITY SHOULD BE CONSTRUCTED. NOT INHERITED.');
  const [heroImageData, setHeroImageData] = useState('');
  const [heroContent, setHeroContent] = useState({
    badge: '/// CHAPTER ZERO ///',
    title: 'WEAR THE',
    titleHighlight: 'OCCULT',
    titleLine2: 'STRUCTURE',
    subtitle: 'Restricted archive of transmitive garments. Each artifact carries a hidden frequency.',
    buttonPrimary: 'EXPLORE CHAPTERS',
    buttonSecondary: 'VIEW MANIFESTO'
  });
  const [aboutSections, setAboutSections] = useState([
    { icon: '⛧', title: 'Our Philosophy', content: 'SKULLTURE was created in opposition to unconscious identity. We believe most people are shaped by systems they never intentionally chose.' },
    { icon: '✦', title: 'The Mission', content: 'To create garments that serve as symbolic artifacts representing deliberate existence. Each drop is intentional. Each piece makes a statement.' },
    { icon: '⟡', title: 'The Community', content: 'Join a collective of individuals who choose awareness over conformity. Initiation grants access to exclusive drops and hidden transmissions.' },
    { icon: '◈', title: 'The Vision', content: 'To redefine fashion as a medium for self-construction rather than external validation. Every artifact is a tool for intentional identity formation.' }
  ]);
  const [footerContent, setFooterContent] = useState({
    tagline: 'Vignex Skullture',
    contactEmail: 'muttannapd2456@gmail.com',
    contactLocation: 'Banglore',
    copyright: '© SKULLTURE — ARCHIVE ACCESS LEVEL: PUBLIC'
  });

  // Fetch user addresses
  const fetchAddresses = async (currentUserId) => {
    const uid = currentUserId || userId;
    if (!uid) {
      console.log("No user ID available for fetching addresses");
      return;
    }
    try {
      const addressesQuery = query(addressesCollection, where("userId", "==", uid));
      const addressesSnap = await getDocs(addressesQuery);
      const userAddresses = addressesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAddresses(userAddresses);
      const defaultAddr = userAddresses.find(addr => addr.isDefault);
      if (defaultAddr) {
        setSelectedAddress(defaultAddr);
      } else if (userAddresses.length > 0) {
        setSelectedAddress(userAddresses[0]);
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    }
  };

  // Set up auth listener
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setUserId(currentUser.uid);
        setUserEmail(currentUser.email);
        sessionStorage.setItem('skullture_auth', 'verified');
        sessionStorage.setItem('skullture_user', currentUser.email);
        sessionStorage.setItem('skullture_uid', currentUser.uid);
        await fetchAddresses(currentUser.uid);
      } else {
        const storedAuth = sessionStorage.getItem('skullture_auth');
        const storedUid = sessionStorage.getItem('skullture_uid');
        const storedEmail = sessionStorage.getItem('skullture_user');
        if (storedAuth === 'verified' && storedUid) {
          setUserId(storedUid);
          setUserEmail(storedEmail);
          await fetchAddresses(storedUid);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch data from Firestore
  useEffect(() => {
    const fetchData = async () => {
      try {
        const chaptersQuery = query(chaptersCollection, orderBy("order", "asc"));
        const chaptersSnapshot = await getDocs(chaptersQuery);
        const chaptersData = chaptersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setChapters(chaptersData);
        
        const currentUser = auth.currentUser;
        if (currentUser) {
          setUser(currentUser);
          setUserId(currentUser.uid);
          setUserEmail(currentUser.email);
          
          const userProgressRef = doc(db, "userProgress", currentUser.uid);
          const userProgressSnap = await getDoc(userProgressRef);
          if (userProgressSnap.exists()) {
            setUnlockedChapters(userProgressSnap.data().unlockedChapters || []);
          } else {
            await setDoc(userProgressRef, {
              uid: currentUser.uid,
              unlockedChapters: [],
              purchasedArtifacts: [],
              updatedAt: new Date().toISOString()
            });
            setUnlockedChapters([]);
          }
        } else {
          const storedUid = sessionStorage.getItem('skullture_uid');
          if (storedUid) {
            setUserId(storedUid);
            const userProgressRef = doc(db, "userProgress", storedUid);
            const userProgressSnap = await getDoc(userProgressRef);
            if (userProgressSnap.exists()) {
              setUnlockedChapters(userProgressSnap.data().unlockedChapters || []);
            } else {
              setUnlockedChapters([]);
            }
          } else {
            setUnlockedChapters([]);
          }
        }
        
        try {
          const manifestoQuery = query(manifestoLinesCollection, orderBy("order", "asc"));
          const manifestoSnapshot = await getDocs(manifestoQuery);
          if (!manifestoSnapshot.empty) {
            const manifestoData = manifestoSnapshot.docs.map(doc => doc.data().text);
            setManifestoLines(manifestoData);
          }
        } catch (err) {
          console.log("Using default manifesto lines");
        }

        try {
          const sectionsSnapshot = await getDocs(sectionsCollection);
          const allSections = sectionsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

          const topbar1 = allSections.find(s => s.type === 'topbar1');
          if (topbar1) setTopBar1Content(topbar1.content);

          const topbar2 = allSections.find(s => s.type === 'topbar2');
          if (topbar2) setTopBar2Content(topbar2.content);

          const hero = allSections.find(s => s.type === 'hero');
          if (hero) {
            setHeroContent({
              badge: hero.badge || heroContent.badge,
              title: hero.title || heroContent.title,
              titleHighlight: hero.titleHighlight || heroContent.titleHighlight,
              titleLine2: hero.titleLine2 || heroContent.titleLine2,
              subtitle: hero.subtitle || heroContent.subtitle,
              buttonPrimary: hero.buttonPrimary || heroContent.buttonPrimary,
              buttonSecondary: hero.buttonSecondary || heroContent.buttonSecondary
            });
            if (hero.imageData) setHeroImageData(hero.imageData);
          }

          const footer = allSections.find(s => s.type === 'footer');
          if (footer) {
            setFooterContent({
              tagline: footer.tagline || footerContent.tagline,
              contactEmail: footer.contactEmail || footerContent.contactEmail,
              contactLocation: footer.contactLocation || footerContent.contactLocation,
              copyright: footer.copyright || footerContent.copyright
            });
          }

          const aboutCards = allSections.filter(s => s.type === 'about').sort((a, b) => (a.order || 0) - (b.order || 0));
          if (aboutCards.length > 0) {
            setAboutSections(aboutCards.map(s => ({
              icon: s.icon || '⛧',
              title: s.title || '',
              content: s.content || ''
            })));
          }
        } catch (err) {
          console.log("Using default section content", err);
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Auth check
  useEffect(() => {
    const checkAuth = () => {
      if (sessionStorage.getItem('skullture_auth') !== 'verified') {
        navigate('/entry');
      }
    };
    checkAuth();
  }, [navigate]);

  // Rotating manifesto
  useEffect(() => {
    if (manifestoLines.length === 0) return;
    const interval = setInterval(() => {
      setManifestoVisible(false);
      setTimeout(() => {
        setCurrentManifestoIndex(prev => (prev + 1) % manifestoLines.length);
        setManifestoVisible(true);
      }, 500);
    }, 3000);
    return () => clearInterval(interval);
  }, [manifestoLines.length]);

  const fetchUserProfile = async () => {
    const currentUserId = userId || sessionStorage.getItem('skullture_uid');
    if (!currentUserId) {
      alert('Please login to view profile');
      return;
    }
    setProfileLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await axios.get(`${apiBaseUrl}/api/user/profile/${currentUserId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUserProfile(response.data.profile);
      setUserPurchases(response.data.purchases || []);
      setShowProfileModal(true);
      playClickSound();
    } catch (error) {
      console.error('Error fetching profile:', error);
      playErrorSound();
      alert('Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleLogout = () => {
    playClickSound();
    sessionStorage.removeItem('skullture_auth');
    sessionStorage.removeItem('skullture_user');
    sessionStorage.removeItem('skullture_uid');
    navigate('/entry');
  };

  const openChapterModal = (chapter, index) => {
    playClickSound();
    setSelectedChapter(chapter);
    setSelectedChapterIndex(index);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedChapter(null);
    setSelectedChapterIndex(null);
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentChapter(null);
    setPaymentIndex(null);
    setShowAddressModal(false);
    setShowAddAddressForm(false);
  };

  const handleUnlockFromModal = async () => {
    if (selectedChapter && !unlockedChapters.includes(selectedChapterIndex)) {
      const currentUserId = userId || sessionStorage.getItem('skullture_uid');
      if (!currentUserId) {
        alert('Please login to unlock chapters');
        return;
      }
      await fetchAddresses(currentUserId);
      
      if (addresses.length === 0) {
        setShowAddressModal(true);
        setShowAddAddressForm(true);
        closeModal();
        setTimeout(() => {
          setPaymentChapter(selectedChapter);
          setPaymentIndex(selectedChapterIndex);
          setShowPaymentModal(true);
        }, 300);
      } else {
        closeModal();
        setTimeout(() => {
          setPaymentChapter(selectedChapter);
          setPaymentIndex(selectedChapterIndex);
          setShowPaymentModal(true);
        }, 300);
      }
    }
  };

  const handleAddAddress = async () => {
    const currentUserId = userId || sessionStorage.getItem('skullture_uid');
    if (!currentUserId) {
      alert('Please login to add address');
      return;
    }
    
    if (!newAddress.fullName || !newAddress.phone || !newAddress.addressLine || !newAddress.city || !newAddress.pincode) {
      alert('Please fill all required fields');
      return;
    }
    
    try {
      const addressData = {
        userId: currentUserId,
        ...newAddress,
        createdAt: new Date().toISOString()
      };
      
      const docRef = await addDoc(addressesCollection, addressData);
      const addedAddress = { id: docRef.id, ...addressData };
      
      setAddresses([...addresses, addedAddress]);
      setSelectedAddress(addedAddress);
      setNewAddress({
        fullName: '',
        phone: '',
        addressLine: '',
        city: '',
        state: '',
        pincode: '',
        isDefault: false
      });
      setShowAddAddressForm(false);
      playSuccessSound();
      alert('Address added successfully!');
    } catch (error) {
      console.error("Error adding address:", error);
      playErrorSound();
      alert('Failed to add address. Please try again.');
    }
  };

  const selectAddressForPayment = (address) => {
    setSelectedAddress(address);
    setShowAddressModal(false);
  };

  // Real Razorpay payment processing
  const processPayment = async () => {
    const currentUserId = userId || sessionStorage.getItem('skullture_uid');
    const currentUserEmail = userEmail || sessionStorage.getItem('skullture_user');
    
    if (!paymentChapter || !currentUserId) {
      playErrorSound();
      alert('Please login to continue');
      return;
    }

    if (!selectedAddress) {
      setShowAddressModal(true);
      return;
    }

    setProcessingPayment(true);
    const amount = paymentChapter.price || 499;
    const chapterName = paymentChapter.name;

    try {
      const token = await auth.currentUser?.getIdToken();
      
      const orderResponse = await axios.post(`${apiBaseUrl}/api/create-order`, {
        chapterId: paymentChapter.id,
        chapterName: chapterName,
        amount: amount,
        userId: currentUserId,
        address: selectedAddress
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (!orderResponse.data.success) {
        throw new Error('Failed to create order');
      }

      const { orderId, amount: orderAmount, keyId } = orderResponse.data;

      const options = {
        key: keyId,
        amount: orderAmount,
        currency: 'INR',
        name: 'SKULLTURE',
        description: `Unlock Chapter: ${chapterName}`,
        image: logoImage,
        order_id: orderId,
        handler: async (response) => {
          const verifyRes = await axios.post(`${apiBaseUrl}/api/verify-payment`, {
            orderId: response.razorpay_order_id,
            paymentId: response.razorpay_payment_id,
            signature: response.razorpay_signature,
            chapterId: paymentChapter.id,
            chapterName: chapterName,
            userId: currentUserId,
            amount: amount,
            address: selectedAddress
          }, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });

          if (verifyRes.data.success) {
            playSuccessSound();
            playUnlockSound();
            alert(`✅ Payment Successful!\n\nChapter "${chapterName}" has been unlocked!\n\nPayment ID: ${response.razorpay_payment_id}`);
            
            const newUnlockedChapters = [...unlockedChapters, paymentIndex];
            setUnlockedChapters(newUnlockedChapters);
            
            const userProgressRef = doc(db, "userProgress", currentUserId);
            await setDoc(userProgressRef, {
              uid: currentUserId,
              unlockedChapters: newUnlockedChapters,
              updatedAt: new Date().toISOString()
            }, { merge: true });
            
            closePaymentModal();
            setSelectedChapter(paymentChapter);
            setSelectedChapterIndex(paymentIndex);
            setShowModal(true);
          } else {
            throw new Error('Payment verification failed');
          }
        },
        prefill: {
          name: selectedAddress.fullName,
          email: currentUserEmail,
          contact: selectedAddress.phone
        },
        theme: {
          color: '#e11d1d'
        },
        modal: {
          ondismiss: () => {
            setProcessingPayment(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Payment error:', error);
      playErrorSound();
      alert('Payment failed. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const toggleMobileMenu = () => {
    if (navMenuRef.current) {
      navMenuRef.current.classList.toggle('open');
    }
  };

  const smoothScroll = (e, targetId) => {
    e.preventDefault();
    playClickSound();
    const target = document.querySelector(targetId);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
      if (navMenuRef.current) navMenuRef.current.classList.remove('open');
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <CustomCursor />
        <div className="skull-loader">
          <img src={logoImage} alt="SKULLTURE" className="logo-img"
            onError={(e) => {
              e.target.style.display = 'none';
              const parent = e.target.parentElement;
              const span = document.createElement('span');
              span.className = 'logo-text-fallback';
              span.textContent = 'SKULLTURE';
              parent.insertBefore(span, e.target);
            }}
          />
          <br />
          <p>LOADING ARCHIVE...</p>
        </div>
      </div>
    );
  }

  const isUnlocked = (index) => unlockedChapters.includes(index);

  return (
    <div className="homepage">
      <CustomCursor />

      <div className="animated-bg">
        <div className="gradient"></div>
      </div>
      <div className="orb orb-1"></div>
      <div className="orb orb-2"></div>

      <div className="top-bar">
        <div className="top-bar-text"><span>⚡⚡</span>{topBar1Content} <span>⚡⚡</span></div>
      </div>

      <div className="header">
        <div className="header-inner">
          <button className="menu-icon" onClick={toggleMobileMenu}>☰</button>
          <div className="nav" ref={navMenuRef}>
            <a href="#timeline" onClick={(e) => smoothScroll(e, '#timeline')}>CHAPTERS</a>
            <a href="#about" onClick={(e) => smoothScroll(e, '#about')}>ABOUT</a>
            <a href="#manifesto" onClick={(e) => smoothScroll(e, '#manifesto')}>MANIFESTO</a>
          </div>
          <img src={logoImage} alt="SKULLTURE" className="logo-img"
            onError={(e) => {
              e.target.style.display = 'none';
              const parent = e.target.parentElement;
              const span = document.createElement('span');
              span.className = 'logo-text-fallback';
              span.textContent = 'SKULLTURE';
              parent.insertBefore(span, e.target);
            }}
          />
          <div className="header-actions">
            <button className="profile-btn" onClick={fetchUserProfile}>
              👤 PROFILE
            </button>
            <SoundToggle />
            <button className="logout-btn" onClick={handleLogout}>⟡ LOGOUT ⟡</button>
          </div>
        </div>
      </div>

      <div className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">{heroContent.badge}</div>
            <h1>
              {heroContent.title} <span>{heroContent.titleHighlight}</span>
              {heroContent.titleLine2 && <><br />{heroContent.titleLine2}</>}
            </h1>
            <p>{heroContent.subtitle}</p>
            <div className="hero-buttons">
              <a href="#timeline" className="btn-primary" onClick={(e) => smoothScroll(e, '#timeline')}>
                {heroContent.buttonPrimary}
              </a>
              <a href="#manifesto" className="btn-secondary" onClick={(e) => smoothScroll(e, '#manifesto')}>
                {heroContent.buttonSecondary}
              </a>
            </div>
          </div>
          <div className="hero-image">
            <img 
              src={heroImageData || "https://images.pexels.com/photos/3998261/pexels-photo-3998261.jpeg?auto=compress&cs=tinysrgb&w=600"} 
              alt="Skull Archive" 
            />
          </div>
        </div>
      </div>

      <div className="top-bar">
        <div className="top-bar-text"><span>⛧ ⛧ </span>{topBar2Content}<span> ⛧ ⛧</span></div>
      </div>

      <div className="timeline-section" id="timeline">
        <h2 className="section-heading">THE <span>CHAPTERS</span></h2>
        <div className="timeline">
          {chapters.map((chapter, index) => {
            const unlocked = isUnlocked(index);
            return (
              <div key={chapter.id} className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}>
                <div className="timeline-content-wrapper">
                  <div className={`timeline-content ${!unlocked ? 'locked' : ''}`} onClick={() => openChapterModal(chapter, index)}>
                    <div className="timeline-chapter">
                      {!unlocked && <span className="lock-icon">🔒</span>}
                      {` /// ${chapter.tag || `CHAPTER ${index + 1}`} ///`}
                    </div>
                    <h3>{chapter.name}</h3>
                    <p>{chapter.description?.substring(0, 120)}...</p>
                    <span className="timeline-badge">
                      {unlocked ? ' ✓ UNLOCKED' : ` 🔒 LOCKED - ₹${chapter.price || 499}`}
                    </span>
                  </div>
                </div>
                <div className="timeline-dot"></div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="about-section" id="about">
        <div className="about-container">
          <h2 className="about-title">THE <span>ARCHIVE</span></h2>
          <div className="about-grid">
            {aboutSections.map((card, idx) => (
              <div className="about-card" key={idx}>
                <div className="about-icon">{card.icon}</div>
                <h3>{card.title}</h3>
                <p>{card.content}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="manifesto-section" id="manifesto">
        <div className="manifesto-container">
          <h2 className="section-heading manifesto-heading">THE <span>MANIFESTO</span></h2>
          <div className="manifesto-rotating">
            <div className={`manifesto-line-wrapper ${manifestoVisible ? 'visible' : 'hidden'}`}>
              <div className="manifesto-line">
                {manifestoLines[currentManifestoIndex]}
              </div>
            </div>
          </div>
          <div className="manifesto-dots">
            {manifestoLines.map((_, i) => (
              <span
                key={i}
                className={`manifesto-dot-indicator ${i === currentManifestoIndex ? 'active' : ''}`}
                onClick={() => { setManifestoVisible(false); setTimeout(() => { setCurrentManifestoIndex(i); setManifestoVisible(true); }, 300); }}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="footer">
        <div className="footer-inner">
          <div>
            <div className="footer-logo">SKULLTURE</div>
            <p style={{ fontSize: '11px', color: '#666' }}>{footerContent.tagline}</p>
          </div>
          <div><h5>ARCHIVE</h5><p>Chapters</p><p>Manifesto</p></div>
          <div><h5>INITIATION</h5><p>Dossier</p><p>Ranks</p></div>
          <div>
            <h5>CONTACT</h5>
            <p>{footerContent.contactEmail}</p>
            <p>{footerContent.contactLocation}</p>
          </div>
        </div>
        <div className="copyright">{footerContent.copyright}</div>
      </div>

      {/* Chapter Modal - Shows preview for locked chapters */}
      {showModal && selectedChapter && (
        <div className="chapter-modal open" onClick={closeModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{isUnlocked(selectedChapterIndex) ? 'CHAPTER DETAILS' : '🔒 LOCKED CHAPTER'}</h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            <div className="modal-body">
              <div className="modal-image">
                {selectedChapter.imageData ? (
                  <img src={selectedChapter.imageData} alt={selectedChapter.name} />
                ) : (
                  <div className="image-placeholder">📖</div>
                )}
              </div>
              <div className="modal-info">
                <div style={{ color: '#e11d1d', fontSize: '11px', letterSpacing: '3px', marginBottom: '12px' }}>
                  {`/// ${selectedChapter.tag || 'CHAPTER'} ///`}
                </div>
                <h3>{selectedChapter.name}</h3>
                
                {isUnlocked(selectedChapterIndex) ? (
                  <p>{selectedChapter.fullDescription || selectedChapter.description}</p>
                ) : (
                  <>
                    <p style={{ color: '#888' }}>
                      {selectedChapter.description?.substring(0, 100)}...
                    </p>
                    <div className="locked-preview-banner">
                      <p>🔒 This chapter is locked. Purchase to unlock full content.</p>
                    </div>
                  </>
                )}
                
                {!isUnlocked(selectedChapterIndex) && (
                  <button className="unlock-btn-modal" onClick={handleUnlockFromModal}>
                    {`🔓 UNLOCK CHAPTER - ₹${selectedChapter.price || 499}`}
                  </button>
                )}
                
                {isUnlocked(selectedChapterIndex) && (
                  <div className="unlocked-badge">✓ CHAPTER UNLOCKED</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal - Proper Pop-up */}
      {showPaymentModal && paymentChapter && (
        <div className="payment-modal-overlay" onClick={closePaymentModal}>
          <div className="payment-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="payment-modal-header">
              <h3>🔓 UNLOCK CHAPTER</h3>
              <button className="payment-modal-close" onClick={closePaymentModal}>✕</button>
            </div>
            <div className="payment-modal-body">
              <div className="payment-chapter-info">
                {paymentChapter.imageData && (
                  <img src={paymentChapter.imageData} alt={paymentChapter.name} className="payment-chapter-image" />
                )}
                <div className="payment-chapter-icon">📖</div>
                <h4>{paymentChapter.name}</h4>
                <p>{paymentChapter.description?.substring(0, 100)}...</p>
              </div>
              
              <div className="address-section">
                <h4>📦 Delivery Address</h4>
                {selectedAddress ? (
                  <div className="selected-address">
                    <p><strong>{selectedAddress.fullName}</strong></p>
                    <p>{selectedAddress.addressLine}</p>
                    <p>{selectedAddress.city}, {selectedAddress.state} - {selectedAddress.pincode}</p>
                    <p>Phone: {selectedAddress.phone}</p>
                    <button className="change-address-btn" onClick={() => setShowAddressModal(true)}>Change Address</button>
                  </div>
                ) : (
                  <button className="add-address-btn" onClick={() => setShowAddressModal(true)}>+ Add Delivery Address</button>
                )}
              </div>
              
              <div className="payment-details">
                <div className="payment-amount">
                  <span>Chapter Price:</span>
                  <strong>{`₹${paymentChapter.price || 499}`}</strong>
                </div>
                <div className="payment-methods">
                  <p>Secure Payment via:</p>
                  <div className="payment-icons">
                    <span>💳 Credit/Debit Card</span>
                    <span>🏦 UPI</span>
                    <span>📱 PhonePe</span>
                    <span>💸 Google Pay</span>
                    <span>🏦 Net Banking</span>
                  </div>
                </div>
              </div>
              
              <button 
                className="payment-confirm-btn" 
                onClick={processPayment}
                disabled={processingPayment || !selectedAddress}
              >
                {processingPayment ? '⏳ PROCESSING...' : `✅ PAY ₹${paymentChapter.price || 499} & UNLOCK`}
              </button>
              <p className="payment-note">* Secure payment powered by Razorpay</p>
              <p className="payment-note">* You will get instant access after successful payment</p>
            </div>
          </div>
        </div>
      )}

      {/* Address Modal */}
      {showAddressModal && (
        <div className="address-modal open" onClick={() => setShowAddressModal(false)}>
          <div className="address-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="address-modal-header">
              <h3>📦 Select Delivery Address</h3>
              <button className="address-modal-close" onClick={() => setShowAddressModal(false)}>✕</button>
            </div>
            <div className="address-modal-body">
              {!showAddAddressForm ? (
                <>
                  <div className="address-list">
                    {addresses.length === 0 ? (
                      <p className="no-address">No saved addresses. Please add a new address.</p>
                    ) : (
                      addresses.map((addr) => (
                        <div 
                          key={addr.id} 
                          className={`address-item ${selectedAddress?.id === addr.id ? 'selected' : ''}`}
                          onClick={() => selectAddressForPayment(addr)}
                        >
                          <div className="address-radio">
                            <input type="radio" checked={selectedAddress?.id === addr.id} readOnly />
                          </div>
                          <div className="address-details">
                            <p className="address-name"><strong>{addr.fullName}</strong></p>
                            <p className="address-line">{addr.addressLine}</p>
                            <p className="address-city">{addr.city}, {addr.state} - {addr.pincode}</p>
                            <p className="address-phone">Phone: {addr.phone}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <button className="add-new-address-btn" onClick={() => setShowAddAddressForm(true)}>
                    + Add New Address
                  </button>
                  <button className="confirm-address-btn" onClick={() => setShowAddressModal(false)}>
                    Confirm & Continue
                  </button>
                </>
              ) : (
                <>
                  <h4>Add New Address</h4>
                  <div className="address-form">
                    <input type="text" placeholder="Full Name *" value={newAddress.fullName} onChange={(e) => setNewAddress({...newAddress, fullName: e.target.value})} />
                    <input type="tel" placeholder="Phone Number *" value={newAddress.phone} onChange={(e) => setNewAddress({...newAddress, phone: e.target.value})} />
                    <textarea placeholder="Address Line *" value={newAddress.addressLine} onChange={(e) => setNewAddress({...newAddress, addressLine: e.target.value})} />
                    <input type="text" placeholder="City *" value={newAddress.city} onChange={(e) => setNewAddress({...newAddress, city: e.target.value})} />
                    <input type="text" placeholder="State" value={newAddress.state} onChange={(e) => setNewAddress({...newAddress, state: e.target.value})} />
                    <input type="text" placeholder="Pincode *" value={newAddress.pincode} onChange={(e) => setNewAddress({...newAddress, pincode: e.target.value})} />
                    <label className="checkbox-label">
                      <input type="checkbox" checked={newAddress.isDefault} onChange={(e) => setNewAddress({...newAddress, isDefault: e.target.checked})} />
                      Set as default address
                    </label>
                    <div className="address-form-buttons">
                      <button className="cancel-address-btn" onClick={() => setShowAddAddressForm(false)}>Cancel</button>
                      <button className="save-address-btn" onClick={handleAddAddress}>Save Address</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {showProfileModal && userProfile && (
        <div className="profile-modal open" onClick={() => setShowProfileModal(false)}>
          <div className="profile-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="profile-modal-header">
              <h3>👤 USER PROFILE</h3>
              <button className="profile-modal-close" onClick={() => setShowProfileModal(false)}>✕</button>
            </div>
            <div className="profile-modal-body">
              <div className="profile-info">
                <div className="profile-avatar">⛧</div>
                <h4>{userProfile.name || userProfile.email?.split('@')[0]}</h4>
                <p className="profile-email">{userProfile.email}</p>
                <p className="profile-role">{`Role: ${userProfile.role || 'User'}`}</p>
                <p className="profile-joined">{`Joined: ${new Date(userProfile.createdAt).toLocaleDateString()}`}</p>
              </div>
              
              <div className="profile-purchases">
                <h4>📚 PURCHASED CHAPTERS</h4>
                {userPurchases.length === 0 ? (
                  <p className="no-purchases">No purchases yet</p>
                ) : (
                  userPurchases.map((purchase) => (
                    <div key={purchase.id} className="purchase-item">
                      <span className="purchase-chapter">{purchase.chapterName}</span>
                      <span className="purchase-amount">{`₹${purchase.amount}`}</span>
                      <span className="purchase-date">{new Date(purchase.purchasedAt).toLocaleDateString()}</span>
                    </div>
                  ))
                )}
              </div>
              
              <div className="profile-stats">
                <div className="stat">
                  <span className="stat-value">{userProfile.unlockedChapters?.length || 0}</span>
                  <span className="stat-label">Chapters Unlocked</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{userPurchases.length}</span>
                  <span className="stat-label">Total Purchases</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{`₹${userPurchases.reduce((sum, p) => sum + (p.amount || 0), 0)}`}</span>
                  <span className="stat-label">Total Spent</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;